import React, { useState } from 'react';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Fab,
    CardActionArea,
    CardActions,
    Tabs,
    Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Box from "@mui/material/Box";
import { fetchCourse, fetchCourseChats, fetchModes } from "../services/MentorApiService";
import { Chat, Course, Mode } from "../types/courseTypes";
import {useNavigate, useParams} from "react-router-dom";
import ChatDialog from "./ChatDialog";
import { formatDate } from "../utils/dateFormatter";

const ChatCatalog = () => {
    const courseId = useParams<{ id: string }>().id!;
    const [course, setCourse] = useState<Course | null>(null);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [modes, setModes] = useState<Mode[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState('');
    const [chats, setChats] = useState<Chat[]>([]);
    const [tabValue, setTabValue] = useState(2);
    const navigate = useNavigate();

    const handleFetchCourse = () => {
        fetchCourse(parseInt(courseId, 10))
            .then((response) => {
                setCourse(response.data);
                setErrorMessage('');
            })
            .catch((error) => {
                setSnackbarOpen(true);
                setErrorMessage(error);
            });
    };

    const handleFetchModes = () => {
        fetchModes()
            .then((response) => {
                setModes(response.data.modes);
                setErrorMessage("");
            })
            .catch((error) => {
                setSnackbarOpen(true);
                setErrorMessage(error);
            });
    };

    const handleFetchCourseChats = () => {
        fetchCourseChats(parseInt(courseId, 10))
            .then((response) => {
                setChats(response.data.chats);
                setErrorMessage('');
            })
            .catch((error) => {
                setSnackbarOpen(true);
                setErrorMessage(error);
            });
    };

    React.useEffect(() => {
        handleFetchCourse();
        handleFetchCourseChats();
        handleFetchModes();
    }, []);

    const handleOpenChatDialog = () => {
        setOpenChatDialog(true);
    }

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
    }

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        if (newValue === 0) {
            navigate(`/courses/${courseId}/form`);
        } else {
            setTabValue(newValue);
        }
    };

    // Filter chats based on tab (assuming chatType exists in Chat type)
    const filteredChats = chats.filter(chat => {
        if (tabValue === 1) return chat.modeId === 3; // AI Literacy Insights
        if (tabValue === 2) return chat.modeId !== 3; // Enhancement Suggestions
        return true; // Default case (should not occur)
    });

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
                {`${course?.courseName} Chat Catalog`}
            </Typography>
            <Tabs value={tabValue} onChange={handleTabChange} centered sx={{ mb: 4 }}>
                <Tab label="Course Info" />
                <Tab label="Evaluation Chats" />
                <Tab label="Suggestion Chats" />
            </Tabs>
            {filteredChats.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        {`Welcome to your courses chat catalog. Here is where you can view and access all your chats created for this course.
                        Due to the scope of the project, shorter conversations are favoured, for more insightful AI feedback.
                        Hence you may wish to start a new chat for your course, if you experience any issues with AI responses due to long conversations.
                        You may also wish to start a new chat to try out different chat modes without losing out on any of your previous chats.
                        To create your first chat for this course, please click the '+' button in the bottom right corner of the page.`}
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredChats.map((chat: Chat) => (
                        <Grid size={6} key={chat.id}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                <CardActionArea
                                    href={
                                        tabValue === 1
                                            ? `/courses/${course?.id}/chat/evaluation/${chat.id}`
                                            : `/courses/${course?.id}/chat/${chat.id}`
                                    }

                                >
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {chat.name}
                                        </Typography>
                                        <Chip label={formatDate(chat.createdAt.toString())} sx={{ mr: 1, mb: 1 }} color="primary" variant="outlined" />
                                        <Chip
                                            label={modes.find(mode => mode.id === chat.modeId)?.name || 'Unknown Mode'}
                                            sx={{ mb: 1 }}
                                            color="success"
                                            variant="outlined"
                                        />
                                    </CardContent>
                                </CardActionArea>
                                <CardActions>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <IconButton aria-label="edit" size="small">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton aria-label="delete" size="small" sx={{ ml: 1 }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
                onClick={handleOpenChatDialog}
            >
                <AddIcon />
            </Fab>
            {
                course && <ChatDialog course={course} open={openChatDialog} onClose={handleCloseChatDialog} onConfirm={handleFetchCourseChats} />
            }
        </Container>
    );
};

export default ChatCatalog;